import React, { useEffect, useState, useRef } from 'react'
import {
  StyledAnchorPoint,
  StyledPlaceholder,
  StyledContainer,
  StyledSubNavWrapper,
  StyledNavButtonWrapper,
  StyledNavButton,
  StyledButtonDivider
} from './M017.styles'
import { scrollIt } from '../../../helpers/utils'
import { ComponentHelper } from '../../../helpers/ComponentHelper'
import { trackDataLayerEvent, eventNames } from '../../../helpers/tracking'
import { M017Props, M017Theme } from './types'

/**
 * - M017 is a multi-purpose component, first usage being building out the sticky navigation by sending an array of possible navigatio titles and the desired theme.
 * - You need matching M017 start anchor and M017 end anchor for every navigation title.
 * - M017 sits in a row on tablet & desktop and becomes sticky once the user scrolls below it.
 */

const M017 = ({
  anchorNavigation,
  activeSection,
  anchorName = '',
  isStart = true,
  navActivePos = '',
  onAnchorEntry = null,
  paddingOffset = 61,
  theme = M017Theme.WHITE
}: M017Props): React.ReactElement => {
  const btn = []
  const duration = 500
  const isFixedPosition = navActivePos === 'validPosition' || navActivePos === 'below'
  const [clientSide, setClientSide] = useState(false)

  const navigationNodeRef = useRef(null)
  const nodeRef = useRef(null)
  const navButtonRef = useRef([])

  const anchorLogic = () => {
    const offsetTop =
      anchorNavigation && isFixedPosition
        ? navigationNodeRef.current && navigationNodeRef.current.offsetTop - paddingOffset
        : nodeRef.current && nodeRef.current.offsetTop - paddingOffset
    if (onAnchorEntry) onAnchorEntry(anchorNavigation, offsetTop, anchorName, isStart)
  }

  useEffect(() => {
    setClientSide(true)
    window.addEventListener('resize', anchorLogic)
    return () => {
      window.removeEventListener('resize', anchorLogic)
    }
  }, [])

  const setActive = (nextAnchorName: string, navActivePosition: string) => {
    const nextButtonIndex = anchorNavigation.indexOf(nextAnchorName)
    if (navActivePosition === 'above' && nodeRef.current) {
      nodeRef.current.scrollLeft = 0
    }
    if (btn[nextButtonIndex] && nodeRef.current) {
      const buttonOffsetLeft = btn[nextButtonIndex].offsetLeft
      try {
        nodeRef.current.scroll({
          left: nextButtonIndex !== 0 ? buttonOffsetLeft : buttonOffsetLeft - 15,
          behavior: 'smooth'
        })
      } catch (error) {
        nodeRef.current.scrollLeft = buttonOffsetLeft
      }
    }
  }

  useEffect(() => {
    if (anchorNavigation) {
      setActive(activeSection, navActivePos)
    }
  }, [anchorNavigation, activeSection, navActivePos])

  useEffect(() => {
    setTimeout(() => anchorLogic(), 1000)
  }, [clientSide])

  const getCleanAnchor = (anchorNme: string) => {
    return anchorNme && anchorNme.replace(/\s+/g, '-').toLowerCase()
  }

  const trackNavClickEvent = (linkName: string) => {
    const eventData = { linkName }
    trackDataLayerEvent(eventNames.cmsStickyBarAnchorLink, eventData)
  }

  const navClick = (nextAnchorName: string) => {
    const validAnchorName = getCleanAnchor(nextAnchorName)
    trackNavClickEvent(validAnchorName)
    const elemTop =
      document.getElementById(`${validAnchorName}`) &&
      document.getElementById(`${validAnchorName}`).offsetTop - (paddingOffset - 2)
    scrollIt(elemTop, 0, duration)
  }

  if (!anchorNavigation) {
    const navId = isStart ? { id: `${getCleanAnchor(anchorName)}` } : {}
    return (
      <StyledAnchorPoint
        data-test={`m017-anchorPoint-${getCleanAnchor(anchorName)}`}
        ref={(node) => {
          nodeRef.current = node
        }}
        {...navId}
      />
    )
  }
  if (anchorNavigation.length < 1 || anchorNavigation.length > 6) return null
  return (
    <>
      <StyledPlaceholder
        data-test='m017-container'
        activeSection={activeSection}
        ref={(navigationNode) => {
          navigationNodeRef.current = navigationNode
        }}
      />
      <StyledContainer
        isFixedPosition={isFixedPosition}
        isFixedBelow={navActivePos === 'below'}
        themeMode={theme}
        data-test='m017'
      >
        <StyledSubNavWrapper data-test='m017-nav-wrapper' themeMode={theme}>
          <StyledNavButtonWrapper
            data-test='m017-nav-button-wrapper'
            ref={(node) => {
              nodeRef.current = node
            }}
          >
            {anchorNavigation.map((navItem, index) => {
              const gridClass = ComponentHelper.gridLayout('M017', index, anchorNavigation.length)
              const isActive = navItem === activeSection
              return (
                <StyledNavButton
                  key={navItem}
                  ref={(ref) => {
                    navButtonRef.current[index] = ref
                  }}
                  themeMode={theme}
                  className={`${gridClass}`}
                  isActive={isActive}
                  onClick={() => navClick(navItem)}
                  data-test={`m017-nav-button-${index}`}
                >
                  {navItem}
                  <StyledButtonDivider themeMode={theme} />
                </StyledNavButton>
              )
            })}
          </StyledNavButtonWrapper>
        </StyledSubNavWrapper>
      </StyledContainer>
    </>
  )
}

export default M017
